import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { ProjectAdjustments } from "./ProjectAdjustments";
import { HelpHint } from "../misc/HelpHint";
import { ActivityAuthor } from "../activity/ActivityAuthor";
import { ProjectExpenses } from "./ProjectExpenses";
import { useState } from "react";
import {
  useGetList,
  useDataProvider,
  useNotify,
  useGetIdentity,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { CrmDataProvider } from "../providers/types";
import type { Deal, Sale, Company } from "../types";
import {
  calculateFinance,
  defaultZones,
  FINANCIAL_ZONES,
  type FinancePlan,
  type FinanceZone,
} from "./financeModel";

const money = (value: number) =>
  new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 2,
  }).format(value);
export function ProjectFinance({ project }: { project: Deal }) {
  const [section, setSection] = useState("");
  const { data: identity, isPending: identityPending } = useGetIdentity();
  const { data, isPending, error } = useGetList<FinancePlan>(
    "project_finance_plans",
    {
      filter: { deal_id: project.id },
      sort: { field: "revision", order: "DESC" },
      pagination: { page: 1, perPage: 20 },
    },
    { enabled: identity?.administrator === true },
  );
  if (identityPending || (identity?.administrator && isPending))
    return <p>Загрузка финансов…</p>;
  if (!identity?.administrator) return null;
  if (error)
    return (
      <p role="alert">
        Не удалось загрузить финансовый план. Обновите страницу.
      </p>
    );
  return (
    <div className="min-w-0 space-y-4">
      <Accordion
        type="single"
        collapsible
        value={section}
        onValueChange={setSection}
        className="space-y-3"
      >
        <AccordionItem value="plan" variant="card">
          <AccordionTrigger>
            <span>
              Финансовый план
              <span className="mt-1 block text-xs font-normal text-muted-foreground">
                Бюджет и распределение средств
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent
            forceMount
            hidden={section !== "plan"}
            role="region"
            aria-label="Финансовый план"
          >
            <FinanceEditor
              key={String(project.id) + ":" + (data?.[0]?.revision ?? 0)}
              project={project}
              plans={data ?? []}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="expenses" variant="card">
          <AccordionTrigger>
            <span>
              Расходы
              <span className="mt-1 block text-xs font-normal text-muted-foreground">
                Платежи, чеки и согласование расходов
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent
            forceMount
            hidden={section !== "expenses"}
            role="region"
            aria-label="Расходы проекта"
          >
            <ProjectExpenses project={project} plan={data?.[0]} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="adjustments" variant="card">
          <AccordionTrigger>
            <span>
              Корректировки
              <span className="mt-1 block text-xs font-normal text-muted-foreground">
                Изменения бюджета и история решений
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent
            forceMount
            hidden={section !== "adjustments"}
            role="region"
            aria-label="Корректировки проекта"
          >
            <ProjectAdjustments project={project} plan={data?.[0]} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function FinanceEditor({
  project,
  plans,
}: {
  project: Deal;
  plans: FinancePlan[];
}) {
  const current = plans[0];
  const [editing, setEditing] = useState(!current);
  const [zones, setZones] = useState<FinanceZone[]>(
    current?.zones ?? defaultZones(),
  );
  const [reason, setReason] = useState(
    current ? "" : "Первичный финансовый план",
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const provider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { data: sales = [] } = useGetList<Sale>("sales", {
    filter: { disabled: false },
    sort: { field: "first_name", order: "ASC" },
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: companies = [] } = useGetList<Company>("companies", {
    filter: {},
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 1000 },
  });
  const price = editing
    ? (project.amount ?? 0)
    : Number(current?.price ?? project.amount);
  let calculation: ReturnType<typeof calculateFinance> | undefined;
  let validation = "";
  try {
    calculation = calculateFinance(price, zones);
  } catch (error) {
    validation = error instanceof Error ? error.message : "Проверьте расчёт";
  }
  const updateZone = (index: number, change: Partial<FinanceZone>) =>
    setZones((previous) =>
      previous.map((z, i) => (i === index ? { ...z, ...change } : z)),
    );
  const save = async () => {
    if (validation || !reason.trim() || saving || project.archived_at) return;
    setSaving(true);
    setSaveError("");
    try {
      await provider.saveProjectFinance({
        deal_id: project.id,
        expected_revision: current?.revision ?? 0,
        zones,
        reason,
      });
      await queryClient.invalidateQueries({
        queryKey: ["project_finance_plans"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["financial_analytics"],
      });
      notify("Финансовый план сохранён", { type: "success" });
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : ((error as { message?: string })?.message ??
              "Не удалось сохранить план"),
      );
    } finally {
      setSaving(false);
    }
  };
  const responsibleName = (zone: FinanceZone) =>
    zone.sales_id != null
      ? (() => {
          const sale = sales.find(
            (s) => String(s.id) === String(zone.sales_id),
          );
          return sale
            ? [sale.first_name, sale.last_name].filter(Boolean).join(" ")
            : "Сотрудник #" + zone.sales_id;
        })()
      : zone.company_id != null
        ? (companies.find((c) => String(c.id) === String(zone.company_id))
            ?.name ?? "Подрядчик #" + zone.company_id)
        : "Не назначен";
  if (project.amount == null && !current)
    return (
      <p className="rounded-xl border p-4 text-sm text-muted-foreground">
        Финансовый план станет доступен после согласования КП.
      </p>
    );
  return (
    <div className="space-y-5 rounded-2xl border bg-card p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
        <div>
          <div className="flex items-center gap-1">
            <h3 className="font-semibold">Финансовый план проекта</h3>
            <HelpHint>
              Зоны ответственности проекта. Доступно только администратору.
            </HelpHint>
          </div>
        </div>
        {current && !editing && !project.archived_at && (
          <Button
            variant="outline"
            onClick={() => {
              setZones(current.zones);
              setReason("");
              setEditing(true);
            }}
          >
            Изменить план
          </Button>
        )}
      </div>
      {current && Number(current.price) !== project.amount && (
        <p className="rounded-md bg-amber-50 text-amber-900 p-3 text-sm">
          Цена КП в карточке изменилась на{" "}
          {project.amount == null
            ? "ещё не согласовано"
            : money(project.amount)}
          . Сохранённый план рассчитан от {money(Number(current.price))}. Новый
          расчёт появится после сохранения новой версии.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-lg bg-muted p-4">
        <div>
          <p className="text-xs text-muted-foreground">
            Цена КП {editing ? "для расчёта" : "в плане"}
          </p>
          <p className="font-semibold">{money(price)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Плановые бюджеты зон</p>
          <p className="font-semibold">
            {calculation ? money(calculation.allocated) : "—"}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">
            Плановый остаток до налогов и комиссий
          </p>
          <p className="font-semibold">
            {calculation ? money(calculation.profit) : "—"}
          </p>
        </div>
      </div>
      <HelpHint>
        Это плановое распределение цены КП. Подтверждённые расходы и остатки зон
        показаны ниже.
      </HelpHint>
      <div className="space-y-3">
        {zones.map((zone, index) => (
          <div key={zone.key} className="rounded-lg border p-3 space-y-3">
            <div className="flex justify-between gap-3">
              <h4 className="font-medium">
                {FINANCIAL_ZONES.find((z) => z.key === zone.key)?.label}
              </h4>
              <span className="font-medium">
                {calculation ? money(calculation.budgets[index]) : "—"}
              </span>
            </div>
            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-3">
                <label className="text-sm space-y-1">
                  <span>Доля, %</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={Number.isNaN(zone.percent) ? "" : zone.percent}
                    disabled={saving || !!project.archived_at}
                    onChange={(event) =>
                      updateZone(index, {
                        percent:
                          event.target.value === ""
                            ? NaN
                            : Number(event.target.value),
                      })
                    }
                  />
                </label>
                <label className="text-sm space-y-1">
                  <span>Ответственный за зону</span>
                  <select
                    className="w-full border rounded-md h-9 bg-background px-2"
                    disabled={saving || !!project.archived_at}
                    value={
                      zone.sales_id != null
                        ? "sales:" + zone.sales_id
                        : zone.company_id != null
                          ? "company:" + zone.company_id
                          : ""
                    }
                    onChange={(event) => {
                      const [kind, id] = event.target.value.split(":");
                      updateZone(index, {
                        sales_id: kind === "sales" ? Number(id) : null,
                        company_id: kind === "company" ? Number(id) : null,
                      });
                    }}
                  >
                    <option value="">Не назначен</option>
                    <optgroup label="Сотрудники">
                      {sales.map((s) => (
                        <option key={s.id} value={"sales:" + s.id}>
                          {s.first_name} {s.last_name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Компании и подрядчики">
                      {companies.map((c) => (
                        <option key={c.id} value={"company:" + c.id}>
                          {c.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </label>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {zone.percent}% · {responsibleName(zone)}
              </p>
            )}
          </div>
        ))}
      </div>
      {validation && (
        <p role="alert" className="text-sm text-destructive">
          {validation}
        </p>
      )}
      {editing && !project.archived_at && (
        <div className="space-y-3">
          <label className="text-sm space-y-1 block">
            <span>Основание расчёта / причина изменения</span>
            <Textarea
              maxLength={2000}
              value={reason}
              disabled={saving}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {saveError && (
            <p role="alert" className="text-sm text-destructive">
              {saveError}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              disabled={saving || !!validation || !reason.trim()}
              onClick={save}
            >
              {saving ? "Сохранение…" : "Сохранить финансовый план"}
            </Button>
            {current && (
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setZones(current.zones);
                  setEditing(false);
                  setSaveError("");
                }}
              >
                Отмена
              </Button>
            )}
          </div>
        </div>
      )}
      {project.archived_at && (
        <p className="text-sm text-muted-foreground">
          Проект в архиве. Финансовый план доступен для просмотра.
        </p>
      )}
      {plans.length > 0 && (
        <section className="space-y-2 border-t pt-4">
          <div className="flex items-center gap-1">
            <h4 className="font-medium">История расчётов</h4>
            <HelpHint>
              Последние 20 версий. Каждая версия сохраняет цену КП, доли и
              ответственных.
            </HelpHint>
          </div>
          {plans.map((plan) => (
            <details key={plan.id} className="border rounded-md p-3 text-sm">
              <summary className="cursor-pointer">
                Версия {plan.revision} ·{" "}
                {new Date(plan.created_at).toLocaleString("ru-RU")} ·{" "}
                {money(Number(plan.price))}
              </summary>
              <p className="mt-2 whitespace-pre-line">{plan.reason}</p>
              <p className="text-xs text-muted-foreground">
                Автор: <ActivityAuthor id={plan.created_by_sales_id} />
              </p>
              {plan.zones.map((zone) => (
                <p key={zone.key} className="mt-1">
                  {FINANCIAL_ZONES.find((z) => z.key === zone.key)?.label}:{" "}
                  {zone.percent}% · {money(Number(zone.budget))} ·{" "}
                  {responsibleName(zone)}
                </p>
              ))}
            </details>
          ))}
        </section>
      )}
    </div>
  );
}
